import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";

import type { Dish, DishIngredient } from "@dashboard/types/dish";
import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { DishReducerAction } from "../reducers/dishReducer";
import type { ErrorReducerAction } from "../reducers/errorReducer";
import { addError } from "./addErrorAction";
import { fetchResolvedEdibles } from "./resolvedEdibleActions";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction
>;

export type DishThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction | ErrorReducerAction
>;

export const setDish: (dish: Dish, isNew: boolean) => ThunkActionReturn = (
  dish: Dish,
  isNew: boolean
) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    return (
      dishApi
        .setDish(dish, isNew)
        // refresh edibles after each change, since that data is now stale
        // resolvedEdibles state must be updated before this new dish is added
        // to the state, to ensure all UI compoenents find the new product
        // in the resolvedEdibles state
        .then(() => dispatch(fetchResolvedEdibles()))
        .then(() => {
          dispatch({ type: "SET_DISH", payload: dish });
        })
        .catch(addError(dispatch))
    );
  };
};

// Specialized set dish action that only sets the dish ingredients
// and doesn't need the complete dish object
export const setDishIngredients: (
  dishes: {
    dishId: string;
    ingredients: DishIngredient[];
  }[]
) => ThunkActionReturn = (
  dishes: {
    dishId: string;
    ingredients: DishIngredient[];
  }[]
) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    const restaurantUuid = getState().restaurant.selectedRestaurant!.uuid;
    return Promise.all(
      dishes.map((dish) =>
        dishApi.setDish(
          {
            // only the fields that are required for setDish
            restaurantUuid,
            id: dish.dishId,
            ingredients: dish.ingredients,
          } as Dish,
          false
        )
      )
    )
      .then(() => dispatch(fetchResolvedEdibles()))
      .then(() => {
        dispatch({ type: "SET_DISH_INGREDIENTS", payload: dishes });
      })
      .catch(addError(dispatch));
  };
};

export const deleteDish: (
  restaurantUuid: string,
  dishId: string
) => ThunkActionReturn = (restaurantUuid: string, dishId: string) => {
  // Reuse the bulkDeleteDishes action to delete the dish
  return bulkDeleteDishes(restaurantUuid, [dishId]);
};

// Deletes multiple dishes at once
export const bulkDeleteDishes: (
  restaurantUuid: string,
  dishIds: string[]
) => ThunkActionReturn = (restaurantUuid: string, dishIds: string[]) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    // Process dishes in batches to avoid too many parallel requests
    const batchSize = 100;
    const totalBatches = Math.ceil(dishIds.length / batchSize);
    let processedCount = 0;

    // Process batches sequentially
    const processBatches = async () => {
      for (let i = 0; i < totalBatches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, dishIds.length);
        const batch = dishIds.slice(start, end);

        // Process each batch in parallel
        await Promise.all(
          batch.map((dishId) => dishApi.deleteDish(restaurantUuid, dishId))
        );

        processedCount += batch.length;
      }
    };

    return (
      processBatches()
        // first remove the dish from the state after the API call is successful
        // so that it is no longer rendered in the UI
        // then call resolvedEdibles to refresh the data (it is now stale)
        .then(() => dispatch({ type: "DELETE_DISH", payload: dishIds }))
        .then(() => dispatch(fetchResolvedEdibles()))
        .catch(addError(dispatch))
    );
  };
};

// Downloads all dishes
// We don't use snapshotListeners here because we don't want to keep listening to changes
export const fetchDishes: () => ThunkActionReturn = () => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    // get current restaurant from state. this allows for running fetchDishes
    // as a refresh operation even when restaurants are changing.
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return dishApi
      .fetchDishes(currentRest.uuid)
      .then((dishes) => {
        dispatch({ type: "FETCHED_DISHES", payload: dishes });
      })
      .catch(addError(dispatch));
  };
};
